import { NavLink, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

import { InferenceQueue } from './InferenceQueue';
import { InferenceRuleLibrary } from './InferenceRuleLibrary';
import { InferenceSandbox } from './InferenceSandbox';

const BASE = '/studio/inference';

/**
 * Spec S09 §9.6 — Inference governance shell with three sub-surfaces:
 *   - /studio/inference          → Discovery queue (default)
 *   - /studio/inference/rules    → Standing rule library
 *   - /studio/inference/sandbox  → CONSTRUCT preview
 *
 * Uses absolute paths in the NavLink tabs (instead of relative `.` /
 * `rules`) because relative resolution interacts poorly with the
 * parent `inference/*` splat route and would otherwise blank the
 * content on tab switches.
 */
export function InferenceRules() {
  return (
    <InferenceShell>
      <Routes>
        <Route index element={<InferenceQueue />} />
        <Route path="rules" element={<InferenceRuleLibrary />} />
        <Route path="sandbox" element={<InferenceSandbox />} />
      </Routes>
    </InferenceShell>
  );
}

function InferenceShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('studio');

  const tabs = [
    { to: BASE, label: t('inference.tabs.queue'), end: true },
    { to: `${BASE}/rules`, label: t('inference.tabs.rules'), end: false },
    { to: `${BASE}/sandbox`, label: t('inference.tabs.sandbox'), end: false },
  ];

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-display text-brand-900">{t('inference.title')}</h1>
        <p className="mt-1 text-body text-slate-600">{t('inference.subtitle')}</p>
      </header>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex gap-4" aria-label="Inference sections">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                cn(
                  'border-b-2 px-1 pb-2 text-body transition-colors',
                  isActive
                    ? 'border-accent text-brand-900'
                    : 'border-transparent text-slate-600 hover:text-slate-900',
                )
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {children}
    </div>
  );
}
